import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  updateDoc, 
  increment,
  Timestamp,
  serverTimestamp,
  startAfter,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { db } from './config';
import { TenantReview } from '../../types/portal';
import { ReviewInput } from '../../utils/validation/reviewSchema';

export const reviewService = {
  /**
   * Create a new review and update tenant stats
   */
  async createReview(data: ReviewInput): Promise<string> {
    const reviewData = {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      helpfulCount: 0,
      isVerified: data.isVerified || false,
    };

    // Add review to 'reviews' collection
    const docRef = await addDoc(collection(db, 'reviews'), reviewData);
    
    // Update Tenant stats (Total reviews and Average Rating)
    // In a real high-scale app, this should be a Cloud Function
    // For now we do it here to maintain simplicity
    await this.updateTenantStats(data.tenantId);

    return docRef.id;
  },

  /**
   * Update tenant's average rating and count
   */
  async updateTenantStats(tenantId: string): Promise<void> {
    const q = query(collection(db, 'reviews'), where('tenantId', '==', tenantId));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      await updateDoc(doc(db, 'tenants', tenantId), {
        averageRating: 0,
        reviewCount: 0
      });
      return;
    }

    let totalRating = 0;
    snapshot.forEach(doc => {
      totalRating += doc.data().rating;
    });

    const averageRating = Number((totalRating / snapshot.size).toFixed(1));
    
    await updateDoc(doc(db, 'tenants', tenantId), {
      averageRating,
      reviewCount: snapshot.size
    });
  },

  /**
   * Get reviews for a tenant with pagination
   */
  async getTenantReviews(
    tenantId: string, 
    pageSize: number = 10, 
    lastDoc?: QueryDocumentSnapshot
  ): Promise<{ reviews: TenantReview[], lastVisible: QueryDocumentSnapshot | null }> {
    let q = query(
      collection(db, 'reviews'),
      where('tenantId', '==', tenantId),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;

    const reviews = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      visitDate: doc.data().visitDate ? new Date(doc.data().visitDate) : undefined,
    } as unknown as TenantReview));

    return { reviews, lastVisible };
  },

  /**
   * Mark review as helpful
   */
  async markAsHelpful(reviewId: string): Promise<void> {
    const reviewRef = doc(db, 'reviews', reviewId);
    await updateDoc(reviewRef, {
      helpfulCount: increment(1)
    });
  }
};
